<i18n>
[
    "product__addToBox--label",
    "product__addToBox--labelVariantTwo",
    "product__addToBox--labelVariantThree",
    "product__unpauseSubscription--label",
    "product__reactivateSubscription--label",
    "product__closedBoxCTA--label",
    "product__pausedCTA--label",
    "product__openBoxShippedCTA--label",
    "product__openBoxDeliveredCTA--label",
    "product__openBoxReturnStartedCTA--label",
    "product__cancelledCTA--title",
    "product__error--noSizeSelected",
    "product__addToBoxSuccess--CTA",
    "product__returnStarted--button",
    "product__returned--button",
    "product__delivered--buttonTitle",
    "product__delivered--button",
    "product__deliveredPurchased--buttonTitle",
    "product__deliveredPurchased--button",
    "product__deliveredBoxBalance--buttonTitle",
    "product__deliveredBoxBalance--button",
    "product__deliveredPurchasedBoxBalance--buttonTitle",
    "product__deliveredPurchasedBoxBalance--button",
    "product__nonPurchasableSku--error",
    "product__previouslyPurchased--error",
    "box__purchasePreview--title"
]
</i18n>

<template>
    <div class="c-product-action">
        <BaseSupportingTextError
            :hasError="hasError"
            :errorText="addToBoxError"
            class="u-spacer--1"
        />
        <BaseMarkdown
            v-if="!hasError"
            :source="aboveBoxButtonText"
            class="u-spacer--1 o-text--caption"
        />
        <template v-if="disableBoxActions">
            <div v-if="prospectPdpPromoMessage" class="o-flex-center u-spacer--2">
                <BaseIcon
                    icon="account--promo"
                    color="black"
                    size="16px"
                    class="c-product-action__icon"
                />
                <span class="o-text--heading-5">
                    {{ prospectPdpPromoMessage }}
                </span>
            </div>
            <!-- NU_FEATURE: https://urbnit.atlassian.net/browse/TYP-33821  -->
            <!-- remove below when feature is complete  -->
            <UrbnModalLauncher
                v-if="!isProspectPdpEnabled($route)"
                :modalOptions="boxHomeModal"
                type="link"
                class="c-product-action__link u-spacer--1"
                @click="handleAddToProspectBoxClick"
            >
                <BaseButton>
                    <!-- NU_EXPERIMENT: expPdpButtonText -->
                    <!-- https://urbnit.atlassian.net/browse/TYP-33468 -->
                    {{ $t(prospectButtonText) }}
                </BaseButton>
            </UrbnModalLauncher>
            <template v-else>
                <BaseLink :to="joinRoute" :isButton="true" class="c-product-action__link u-spacer--2">
                    <!-- NU_EXPERIMENT: expPdpButtonText -->
                    <!-- https://urbnit.atlassian.net/browse/TYP-33468 -->
                    {{ $t(prospectButtonText) }}
                </BaseLink>
                <div class="u-text--center o-text--caption u-text--gray">
                    {{ $t('product_prospect--infoBelowCtaRuntime') }}
                </div>
            </template>
        </template>
        <div
            v-show="!disableBoxActions"
            class="u-spacer--1"
            @mouseenter="addToBoxTextColorIsLight = false"
            @mouseleave="addToBoxTextColorIsLight = true"
            @click="updateAboveBoxTextStatus"
        >
            <!-- TODO: make this possible within BaseButton / LoadingButton
            and  make this accessible using v-tab-focus instead of @click
            https://urbnit.atlassian.net/browse/TYP-5133 -->
            <div
                v-if="boxButtonIsDisabled"
                class="c-product-action__button u-text--medium-gray o-flex-vertical-center o-flex-horizontal-center"
            >
                {{ $t(boxButtonText) }}
            </div>
            <LoadingButton
                v-else
                :isLoading="addToBoxIsLoading"
                @click="clickedAddToBox"
            >
                <SuccessCheck
                    ref="successCheckRef"
                    size="16px"
                    :light="addToBoxTextColorIsLight"
                    :text="$t('product__addToBoxSuccess--CTA')"
                >
                    <template #action>
                        {{ $t(boxButtonText) }}
                    </template>
                </SuccessCheck>
            </LoadingButton>
        </div>
    </div>
</template>

<script>
import { get, isEmpty } from 'lodash-es';

import {
    mapState,
    mapGetters,
    mapActions,
} from 'vuex';

import {
    mapInstanceMutations,
    mapInstanceState,
    mapInstanceGetters,
} from '~coreModules/core/js/store-utils';
import { genericRouteNames } from '~coreModules/core/js/router-constants';
import { getProductModuleName } from '~coreRoutes/product/js/product-utils';

import runtimeConfig from '~config/config';
// NU_FEATURE: https://urbnit.atlassian.net/browse/TYP-33821
// remove below when feature is complete
import { isProspectPdpEnabled } from '~routes/product/js/product-utils';
import { getContentfulSlugForSubscriptionStatus } from '~modules/box/js/box-utils';
import { BOX_ORDER_STATUSES } from '~modules/box/js/box-constants';
import { BOX_MODULE_NAME, ADD_BOX_ITEM } from '~modules/box/js/box-store';
import { PROFILE_MODULE_NAME } from '~modules/profile/js/profile-store';
import {
    SET_SKU_CODE,
    SET_ADD_TO_BOX_ERROR,
    UPDATE_ITEM_IN_BOX_STATUS,
} from '~routes/product/js/product-store';
import { MODALS_MODULE_NAME, LAUNCH_MODAL } from '~coreModules/modals/js/modals-store';
import { GLOBAL_EVENT } from '~coreModules/core/js/store';

import { ADD_TO_PROSPECT_BOX_CLICK } from '~modules/core/js/global-event-constants';
import { EXP_PDP_BUTTON_TEXT_VARIANT_MAP } from '~modules/core/js/experiment-constants.js';
import { MODALS } from '~modules/modals/js/modal-constants';
import { rentalRouteNames } from '~modules/core/js/router-constants';

import LoadingButton from '~coreModules/core/components/ui/buttons/LoadingButton.vue';
import BaseButton from '~coreModules/core/components/ui/buttons/BaseButton.vue';
import BaseSupportingTextError from '~coreModules/core/components/ui/form-elements/BaseSupportingTextError.vue';
import UrbnModalLauncher from '~coreModules/modals/components/UrbnModalLauncher.vue';
import BaseMarkdown from '~coreModules/core/components/ui/BaseMarkdown.vue';
import SuccessCheck from '~coreModules/core/components/ui/SuccessCheck.vue';

export default {
    name: 'ProductActionButtons',
    components: {
        BaseSupportingTextError,
        LoadingButton,
        UrbnModalLauncher,
        BaseButton,
        BaseMarkdown,
        SuccessCheck,
    },
    props: {
        selectedChoice: {
            type: Object,
            required: true,
        },
    },
    data: () => ({
        addToBoxIsLoading: false,
        boxHomeModal: MODALS.boxHome,
        addToBoxTextColorIsLight: false,
        joinRoute: { name: genericRouteNames.join.base },
        // NU_FEATURE: https://urbnit.atlassian.net/browse/TYP-33821
        // remove below when feature is complete
        isProspectPdpEnabled,
    }),
    computed: {
        ...mapInstanceState(getProductModuleName, {
            selectedSku: state => state.selectedSku,
            addToBoxError: state => state.addToBoxError,
            catalogResponseId: state => state.catalogResponseId,
        }),
        ...mapInstanceGetters(getProductModuleName, [
            'purchasableSkus',
            'productAnalyticsData',
        ]),
        ...mapState(PROFILE_MODULE_NAME, {
            nextBillingCycle: state => state.nextBillingCycle,
            subscriptionStatus: state => state.subscriptionStatus,
        }),
        ...mapState(BOX_MODULE_NAME, {
            boxOrderStatus: state => state.boxOrderStatus,
            boxBalance: state => get(state, 'boxInfo.boxBalance'),
        }),
        ...mapGetters(PROFILE_MODULE_NAME, [
            'isSubscribed',
            'isCancelledOrCancelRequested',
            'isPausedOrPauseRequested',
            'prospectPdpPromoMessage',
        ]),
        ...mapGetters(BOX_MODULE_NAME, [
            'boxIsOpen',
            'boxIsReturned',
            'boxIsReturnStarted',
            'boxIsDelivered',
            'disableBoxActions',
            'purchasedRecentlyOrderedSkus',
            'showBoxedItems',
        ]),
        hasError() {
            return this.addToBoxError !== '';
        },
        itemInCloset() {
            return this.selectedChoice ? this.selectedChoice.inCloset : false;
        },
        enableBoxButtonWithoutBoxActions() {
            return this.isCancelledOrCancelRequested || this.isPausedOrPauseRequested;
        },
        swapEligible() {
            return this.boxIsDelivered && this.boxBalance > 0;
        },
        swapIneligible() {
            return this.boxIsDelivered && this.boxBalance === 0;
        },
        aboveBoxButtonText() {
            let text = '';

            if (this.isCancelledOrCancelRequested) {
                text = this.$t('product__cancelledCTA--title');
            } else if (this.isPausedOrPauseRequested) {
                text = this.$t('product__pausedCTA--label');
            } else if (this.isSubscribed && this.boxOrderStatus === BOX_ORDER_STATUSES.SHIPPED && this.boxBalance > 0) {
                text = this.$t('product__openBoxShippedCTA--label');
            } else if (this.isSubscribed && this.swapIneligible) {
                if (isEmpty(this.purchasableSkus)) {
                    text = this.$t('product__deliveredPurchased--buttonTitle',
                        [this.$d(new Date(Date.parse(this.nextBillingCycle)), 'yearless')]);
                } else {
                    text = this.$t('product__delivered--buttonTitle');
                }
            } else if (this.isSubscribed && this.swapEligible) {
                if (isEmpty(this.purchasableSkus)) {
                    text = this.$t('product__deliveredPurchasedBoxBalance--buttonTitle');
                } else {
                    text = this.$t('product__deliveredBoxBalance--buttonTitle');
                }
            } else if (this.isSubscribed &&
                this.boxIsReturnStarted && this.boxBalance > 0) {
                text = this.$t('product__openBoxReturnStartedCTA--label');
            } else if (this.isSubscribed && !this.boxIsOpen) {
                text = this.$t('product__closedBoxCTA--label',
                    [this.$d(new Date(Date.parse(this.nextBillingCycle)), 'yearless')]);
            }

            return text;
        },
        ableToRenewEarly() {
            return (this.swapIneligible || this.boxIsReturned) && this.isSubscribed;
        },
        boxButtonIsDisabled() {
            if (this.swapEligible && !isEmpty(this.purchasableSkus)) return false;

            return !(this.showBoxedItems || this.enableBoxButtonWithoutBoxActions || this.ableToRenewEarly);

        },
        // NU_EXPERIMENT: expPdpButtonText
        // https://urbnit.atlassian.net/browse/TYP-33468
        // just use slug we decide on after experiment is over
        prospectButtonText() {
            const { expPdpButtonText } = runtimeConfig.features;
            if (runtimeConfig.features.expPdpButtonText) {
                return EXP_PDP_BUTTON_TEXT_VARIANT_MAP[expPdpButtonText] || 'product__addToBox--label';
            }

            return 'product__addToBox--label';
        },
        boxButtonText() {
            if (this.swapIneligible && this.isSubscribed) {
                if (isEmpty(this.purchasableSkus)) {
                    return 'product__deliveredPurchased--button';
                }
                return 'product__delivered--button';
            }

            if (this.swapEligible && this.isSubscribed) {
                if (isEmpty(this.purchasableSkus)) {
                    return 'product__deliveredPurchasedBoxBalance--button';
                }
                return 'product__deliveredBoxBalance--button';
            }

            if (this.boxIsReturnStarted && this.isSubscribed) {
                return 'product__returnStarted--button';
            }

            if (this.boxIsReturned && this.isSubscribed) {
                return 'product__returned--button';
            }

            if (this.isCancelledOrCancelRequested) {
                return 'product__reactivateSubscription--label';
            }

            if (this.isPausedOrPauseRequested) {
                return 'product__unpauseSubscription--label';
            }

            return 'product__addToBox--label';
        },
    },
    methods: {
        ...mapInstanceMutations(getProductModuleName, {
            setSkuCode: SET_SKU_CODE,
            setErrorText: SET_ADD_TO_BOX_ERROR,
            updateItemInBoxStatus: UPDATE_ITEM_IN_BOX_STATUS,
        }),
        ...mapActions(BOX_MODULE_NAME, {
            addBoxItem: ADD_BOX_ITEM,
        }),
        ...mapActions(MODALS_MODULE_NAME, {
            launchModal: LAUNCH_MODAL,
        }),
        ...mapActions({
            trackGlobalEvent: GLOBAL_EVENT,
        }),
        purchaseItem(sku) {
            this.launchModal({
                ...MODALS.boxPurchaseSlide,
                props: {
                    skuId: sku,
                },
            });
        },
        clickedAddToBox() {
            if (this.swapIneligible && this.isSubscribed) {
                if (isEmpty(this.purchasableSkus)) {
                    return this.$router.push(`#${MODALS.boxRenewEarlyInfo.id}`);
                }

                if (!this.selectedSku) {
                    return this.setErrorText(this.$t('product__error--noSizeSelected'));
                }

                if (this.purchasableSkus.includes(this.selectedSku)) {
                    return this.purchaseItem(this.selectedSku);
                }

                if (this.purchasedRecentlyOrderedSkus.includes(this.selectedSku)) {
                    return this.setErrorText(this.$t('product__previouslyPurchased--error'));
                }

                return this.setErrorText(this.$t('product__nonPurchasableSku--error'));
            }

            if (this.swapEligible && this.isSubscribed) {
                if (!this.selectedSku) {
                    return this.setErrorText(this.$t('product__error--noSizeSelected'));
                }

                if (this.purchasableSkus.includes(this.selectedSku)) {
                    return this.purchaseItem(this.selectedSku);
                }

                if (this.purchasedRecentlyOrderedSkus.includes(this.selectedSku)) {
                    return this.setErrorText(this.$t('product__previouslyPurchased--error'));
                }

                return this.setErrorText(this.$t('product__nonPurchasableSku--error'));
            }

            if (this.boxIsReturned && this.isSubscribed) {
                return this.$router.push(`#${MODALS.boxRenewEarlySlide.id}`);
            }

            if (this.enableBoxButtonWithoutBoxActions && !this.ableToRenewEarly) {
                return this.$router.push({ name: rentalRouteNames.account.subscription });
            }

            if (this.selectedSku) {
                this.addToBoxIsLoading = true;
                return this.addBoxItem({
                    sku: this.selectedSku,
                    productAnalyticsData: this.productAnalyticsData,
                    catalogResponseId: this.catalogResponseId,
                })
                    .then(() => {
                        this.setErrorText('');
                        this.updateItemInBoxStatus(true);
                        this.addToBoxIsLoading = false;
                    })
                    .catch((error) => {
                        this.addToBoxIsLoading = false;
                        this.setErrorText(get(error, 'data.message', ''));
                    })
                    .finally(() => {
                        if (!this.hasError) {
                            this.$refs.successCheckRef.changeSuccessMessageStatus();
                        }
                    });
            }

            this.setErrorText(this.$t('product__error--noSizeSelected'));

            return null;
        },
        updateAboveBoxTextStatus() {
            if (this.swapEligible && !isEmpty(this.purchasableSkus)) {
                return;
            }
            if (this.isSubscribed && !this.boxIsOpen && !this.ableToRenewEarly) {
                this.setErrorText(this.aboveBoxButtonText);
            }
        },
        handleAddToProspectBoxClick() {
            const contentfulSlug = getContentfulSlugForSubscriptionStatus({
                subscriptionStatus: this.subscriptionStatus,
            });

            this.trackGlobalEvent({
                type: ADD_TO_PROSPECT_BOX_CLICK,
                data: {
                    destination: `/rent#${contentfulSlug}`,
                },
            });
        },
    },
};
</script>

<style lang="scss">
    .c-product-action {
        button {
            width: 100%;
        }

        &__link {
            width: 100%;
        }

        &__icon {
            margin-right: $nu-spacer-1;
        }

        &__button {
            cursor: pointer;
            border: 1px solid $nu-gray--medium;
            border-radius: 2px;
            height: 40px;
            background-color: $transparent;
        }
    }
</style>
